import { StorageObjectEventType, type StorageObjectGroupType } from '@navio/nsdk';
import {
  NoddiCheckbox,
  NoddiInfoBanner,
  NoddiSelectionCardGroup,
  NoddiSelectionCardOption,
  NoddiTextArea,
  type SelectOption
} from 'noddi-ui-web';
import { storageEventCopy, storageMoveSectionCopy, withCustomerStorageEventTypes } from 'noddi-util';
import { useState } from 'react';
import { StorageUnitSelect } from '../StorageUnits/StorageUnitSelect';

type Props = {
  selectedEventType: StorageObjectEventType | undefined;
  onEventTypeChange: (value: StorageObjectEventType | undefined) => void;
  storageUnit: SelectOption<number> | undefined;
  onStorageUnitChange: (value: SelectOption<number> | undefined) => void;
  /** Facility the caller works in; unit search starts scoped to it. */
  defaultStorageFacilityId?: number;
  notes: string;
  requireNotes?: boolean;
  onNotesChange: (value: string) => void;
  availableEventTypes: StorageObjectEventType[];
  /** Kinds being moved; units that do not hold one of them cannot be chosen. */
  movingGroupTypes: StorageObjectGroupType[];
  /**
   * If provided, unit search is restricted to these facilities.
   * Mirrors the backend rule: non-superusers can only land wheel sets at
   * the current facility or at a facility on an active transfer order
   * (CREATED or IN_PROGRESS). `undefined` means "no restriction"
   * (superuser).
   */
  allowedFacilityIds?: number[];
  /**
   * When true, render a compact heads-up reminding super users that
   * cross-facility moves are usually planned via a transfer order.
   * Hidden when the selection is already on an active TO (then a
   * cross-facility move is the expected flow). Display-only.
   */
  showSuperUserOverrideHint?: boolean;
};

const MOVE_CARDS: Partial<Record<StorageObjectEventType, NoddiSelectionCardOption<StorageObjectEventType>>> = {
  [StorageObjectEventType.MOUNTED_ON_CAR]: {
    value: StorageObjectEventType.MOUNTED_ON_CAR,
    label: storageEventCopy.mountedOnCar.title,
    description: storageEventCopy.mountedOnCar.description,
    icon: 'Car'
  },
  [StorageObjectEventType.PICKED_UP_BY_CUSTOMER]: {
    value: StorageObjectEventType.PICKED_UP_BY_CUSTOMER,
    label: storageEventCopy.pickedUpByCustomer.title,
    description: storageEventCopy.pickedUpByCustomer.description,
    icon: 'Handshake'
  },
  [StorageObjectEventType.DELIVERED_TO_CUSTOMER]: {
    value: StorageObjectEventType.DELIVERED_TO_CUSTOMER,
    label: storageEventCopy.deliveredToCustomer.title,
    description: storageEventCopy.deliveredToCustomer.description,
    icon: 'PackageCheck'
  },
  [StorageObjectEventType.INTERNAL_DELIVERY]: {
    value: StorageObjectEventType.INTERNAL_DELIVERY,
    label: storageEventCopy.moveToStorageUnit.title,
    description: storageEventCopy.moveToStorageUnit.description,
    icon: 'PackagePlus'
  },
  [StorageObjectEventType.PICKED_UP_FROM_CUSTOMER]: {
    value: StorageObjectEventType.PICKED_UP_FROM_CUSTOMER,
    label: storageEventCopy.pickedUpFromCustomer.title,
    description: storageEventCopy.pickedUpFromCustomer.description,
    icon: 'Package'
  }
};

const MOVE_SECTIONS: { title: string; eventTypes: readonly StorageObjectEventType[] }[] = [
  {
    title: storageMoveSectionCopy.withTheCustomer,
    eventTypes: withCustomerStorageEventTypes
  },
  {
    title: storageMoveSectionCopy.storageUnit,
    eventTypes: [StorageObjectEventType.INTERNAL_DELIVERY]
  },
  {
    title: storageMoveSectionCopy.fromTheCustomer,
    eventTypes: [StorageObjectEventType.PICKED_UP_FROM_CUSTOMER]
  }
];

export const MoveStorageObjectsForm = ({
  selectedEventType,
  onEventTypeChange,
  storageUnit,
  onStorageUnitChange,
  defaultStorageFacilityId,
  notes,
  requireNotes = false,
  onNotesChange,
  availableEventTypes,
  movingGroupTypes,
  allowedFacilityIds,
  showSuperUserOverrideHint
}: Props) => {
  const [searchAllFacilities, setSearchAllFacilities] = useState(false);

  const isDefaultFacilityAllowed =
    defaultStorageFacilityId != null && (!allowedFacilityIds || allowedFacilityIds.includes(defaultStorageFacilityId));
  const canSearchOtherFacilities = isDefaultFacilityAllowed && (!allowedFacilityIds || allowedFacilityIds.length > 1);
  const storageFacilityIds =
    isDefaultFacilityAllowed && !searchAllFacilities ? [defaultStorageFacilityId] : allowedFacilityIds;

  const facilityRestrictionBanner = allowedFacilityIds?.length === 0 && (
    <NoddiInfoBanner
      variant='warning'
      title='No facility available for this move'
      texts={[
        {
          value:
            'Only super users can move wheel sets between facilities. To move across facilities, create a transfer order first — then this picker will offer the destination facility.'
        }
      ]}
    />
  );

  const moveSections = MOVE_SECTIONS.map((section) => ({
    title: section.title,
    options: section.eventTypes.flatMap((eventType) => {
      if (!availableEventTypes.includes(eventType)) {
        return [];
      }
      const card = MOVE_CARDS[eventType];
      return card ? [card] : [];
    })
  })).filter((section) => section.options.length > 0);

  return (
    <div className='flex flex-col gap-5'>
      {moveSections.length > 0 ? (
        <div className='flex flex-col gap-5'>
          <div className='flex flex-col gap-6'>
            {moveSections.map((section) => (
              <NoddiSelectionCardGroup
                key={section.title}
                title={section.title}
                options={section.options}
                value={selectedEventType}
                onValueChange={onEventTypeChange}
                size='small'
                layout='column'
                gridClassName={
                  section.options.length === 1
                    ? 'grid-cols-1'
                    : section.options.length === 2
                      ? 'sm:grid-cols-2'
                      : 'sm:grid-cols-3'
                }
              />
            ))}
          </div>
          {selectedEventType === StorageObjectEventType.INTERNAL_DELIVERY && (
            <>
              {facilityRestrictionBanner}
              {showSuperUserOverrideHint && (
                <NoddiInfoBanner
                  variant='warning'
                  size='small'
                  title='Super-user override enabled'
                  texts={[
                    {
                      value:
                        'Cross-facility moves should use transfer orders. Super users can bypass this for direct moves, but only as an escape hatch.'
                    }
                  ]}
                />
              )}
              {!facilityRestrictionBanner && (
                <div className='flex flex-col gap-2'>
                  <StorageUnitSelect
                    value={storageUnit}
                    onChange={onStorageUnitChange}
                    filters={{ storageFacilityIds }}
                    storedGroupTypes={movingGroupTypes}
                  />
                  {canSearchOtherFacilities && (
                    <NoddiCheckbox
                      label='Search other facilities'
                      checked={searchAllFacilities}
                      onCheckedChange={(checked) => {
                        setSearchAllFacilities(checked);
                        onStorageUnitChange(undefined);
                      }}
                    />
                  )}
                </div>
              )}
            </>
          )}
          <NoddiTextArea
            name='move-storage-objects-notes'
            label={requireNotes ? 'Note' : 'Note (optional)'}
            placeholder='Add a note for this event'
            value={notes}
            onChange={(event) => onNotesChange(event.target.value)}
          />
        </div>
      ) : (
        <NoddiInfoBanner
          variant='info'
          title='No actions available'
          texts={[
            {
              value:
                'This selection has conflicting permissions - meaning we cant find a valid action in order to move them in bulk. Check the move list below for more info'
            }
          ]}
        />
      )}
    </div>
  );
};
